import { WorkspaceType } from "@/constants/workplaces.ts";
import type { WorkspaceNode } from "@/_store/msp/msp-treeview.module.ts";

/**
 * Clones a WorkspaceNode and its children recursively.
 * It is 2.5x faster than cloneDeep
 */
function cloneNode(node: WorkspaceNode): WorkspaceNode {
  const cloned: WorkspaceNode = {
    workspaceId: node.workspaceId,
    parentWorkspaceId: node.parentWorkspaceId,
    descendantCount: node.descendantCount,
    displayName: node.displayName,
    type: node.type,
    subscriptionType: node.subscriptionType,
    mspPolicyId: node.mspPolicyId,
    mspPolicyName: node.mspPolicyName,
    // Shallow-clone subscription and labels to avoid shared references between tree copies
    subscription: node.subscription ? { ...node.subscription } : undefined,
    labels: node.labels ? [...node.labels] : undefined,
    trialEndDate: node.trialEndDate,
  };

  if (node.children && node.children.length > 0) {
    cloned.children = node.children.map(cloneNode);
  }

  return cloned;
}

/**
 * Processes a root WorkspaceNode for display in a tree structure.
 *
 * This function performs two key operations:
 * 1.  **Clones: ** It creates a deep copy of the `rootNode` to avoid mutating the
 * original data.
 * 2.  **Sorts: ** It recursively traverses the entire tree (depth-first) and sorts
 * the `children` array of each node.
 *
 * The sorting logic is as follows:
 * - Nodes with `type === WorkspaceType.CHANNEL` are always sorted to the top.
 * - All other node types are sorted below the channels.
 * - Within each group (channels and non-channels), nodes are sorted
 * alphabetically by their `displayName`.
 *
 * This function does NOT remove or modify `children` arrays, even if they are empty.
 *
 * @param {WorkspaceNode} rootNode - The root node of the workspace tree.
 * @returns {WorkspaceNode[]} An array containing the single, processed
 * (cloned and sorted) root node. This format is often required
 * by UI components like `v-treeview` that expect an array of root items.
 */
export function formatWorkspaceTree(rootNode: WorkspaceNode): WorkspaceNode[] {
  // Create a deep copy to ensure the original data is not mutated.
  // Using custom cloneNode instead of lodash cloneDeep for better performance.
  // performance on 800 entries when using cloneNode is 4secs in comparison with 11 secs with cloneDeep
  const nodeCopy = cloneNode(rootNode);
  /**
   * Inner recursive function to traverse and sort the node tree.
   * This function MUTATES the node it is given (which is a clone).
   * It uses a depth-first approach, sorting the deepest children first.
   * @param node - The current WorkspaceNode to process.
   */
  function sortChildrenRecursively(node: WorkspaceNode) {
    // If there are no children, there is nothing to sort.
    if (!node.children || node.children.length === 0) {
      return;
    }

    // --- RECURSE FIRST ---
    // Process all children nodes before sorting the current level.
    // This ensures the entire tree is processed from the bottom up.
    for (let i = 0; i < node.children.length; i++) {
      sortChildrenRecursively(node.children[i]);
    }

    // --- SORT CURRENT LEVEL ---
    // Now, sort the children of the current node.
    node.children.sort((a, b) => {
      const aIsChannel = a.type === WorkspaceType.CHANNEL;
      const bIsChannel = b.type === WorkspaceType.CHANNEL;

      // Group 1: Channels (e.g., 'a' is a channel, 'b' is not)
      if (aIsChannel && !bIsChannel) {
        return -1; // 'a' (channel) comes before 'b' (child)
      }

      // Group 2: Children (e.g., 'a' is not a channel, 'b' is)
      if (!aIsChannel && bIsChannel) {
        return 1; // 'b' (channel) comes before 'a' (child)
      }

      // Same Group (both are channels OR both are not):
      // Sort alphabetically by their display name.
      return a.displayName.localeCompare(b.displayName);
    });
  }

  // Start the recursive sorting process on the cloned root node.
  sortChildrenRecursively(nodeCopy);
  // Return the processed copy, wrapped in an array.
  return [nodeCopy];
}
